import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, KeyRound } from "lucide-react";
import { PASSWORD_MIN } from "../../shared/contracts.js";
import { api, errorMessage, isApiError } from "../api.js";
import { PasswordInput, PasswordStrength } from "../components/PasswordFields.js";
import { Button, ButtonLink } from "../components/ui/Button.js";
import { Field } from "../components/ui/Form.js";
import { Callout, LoadingBlock } from "../components/ui/States.js";
import { useToast } from "../components/ui/Toast.js";
import { fmtDateTime } from "../lib/format.js";
import { useDocumentTitle } from "../lib/hooks.js";
import { assessPassword } from "../lib/password.js";
import { AuthLayout } from "./AuthLayout.js";

/**
 * Lee el token del fragmento (#t=…). El fragmento nunca viaja al servidor ni queda
 * en los registros de Caddy; igual se borra de la barra de direcciones y del
 * historial apenas se lee, para que no quede a la vista ni se comparta por error.
 */
function readTokenFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;
  const t = new URLSearchParams(hash).get("t");
  return t && t.length >= 20 && t.length <= 200 ? t : null;
}

export function SetPasswordPage() {
  useDocumentTitle("Definir contraseña");
  const [token] = useState(readTokenFromHash);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
  }, []);

  const inspect = useQuery({
    // El token es secreto: la clave de caché no lo incluye y la consulta no se reintenta.
    queryKey: ["auth", "token-inspect"],
    queryFn: () => api.auth.inspectToken(token ?? ""),
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; form?: string }>({});

  const redeem = useMutation({
    mutationFn: api.auth.redeemToken,
    onSuccess: () => {
      // Cualquier sesión previa en este navegador ya no corresponde (puede ser otra persona).
      qc.clear();
      toast.success("Contraseña definida. Ya puedes ingresar.");
      void navigate("/login", { replace: true, state: { email: inspect.data?.email } });
    },
    onError: (err) => {
      if (isApiError(err, 400)) {
        const fe = err.fieldErrors;
        setErrors({ password: fe.newPassword ?? errorMessage(err) });
      } else if (isApiError(err, 404) || isApiError(err, 410)) {
        setErrors({ form: "El enlace expiró o ya fue usado. Pide uno nuevo a un administrador." });
      } else {
        setErrors({ form: errorMessage(err) });
      }
    },
  });

  if (!token) {
    return (
      <AuthLayout title="Enlace no válido">
        <InvalidLink />
      </AuthLayout>
    );
  }
  if (inspect.isPending) {
    return (
      <AuthLayout title="Definir contraseña">
        <LoadingBlock label="Verificando el enlace…" />
      </AuthLayout>
    );
  }
  if (inspect.isError) {
    return (
      <AuthLayout title="Enlace no válido">
        {isApiError(inspect.error, 404) ? <InvalidLink /> : (
          <Callout tone="danger" icon={<AlertTriangle />}>{errorMessage(inspect.error)}</Callout>
        )}
      </AuthLayout>
    );
  }

  const info = inspect.data;
  const isInvite = info.purpose === "invite";

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const a = assessPassword(password, { email: info.email, fullName: info.fullName });
    const next: typeof errors = {};
    if (!a.valid) next.password = a.hints[0] ?? `Usa al menos ${PASSWORD_MIN} caracteres.`;
    if (confirm !== password) next.confirm = "Las contraseñas no coinciden.";
    setErrors(next);
    if (Object.keys(next).length > 0 || !token) return;
    redeem.mutate({ token, newPassword: password });
  }

  return (
    <AuthLayout
      title={isInvite ? "Bienvenido/a a SENTINEL" : "Restablecer contraseña"}
      subtitle={
        <>
          {isInvite ? "Define la contraseña de tu cuenta" : "Define una nueva contraseña"} para{" "}
          <strong className="text-fg">{info.fullName}</strong> ({info.email}). El enlace vence el{" "}
          {fmtDateTime(info.expiresAt)}.
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {/* Campo oculto con el correo: los gestores de contraseñas asocian la clave a la cuenta correcta. */}
        <input type="email" name="username" autoComplete="username" value={info.email} readOnly hidden />
        <Field label="Nueva contraseña" required error={errors.password}>
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={256}
              autoFocus
            />
          )}
        </Field>
        <PasswordStrength password={password} email={info.email} fullName={info.fullName} />
        <Field label="Repite la contraseña" required error={errors.confirm}>
          {(p) => (
            <PasswordInput
              {...p}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              maxLength={256}
            />
          )}
        </Field>
        {errors.form && (
          <Callout tone="danger" icon={<AlertTriangle />}>
            {errors.form}
          </Callout>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={redeem.isPending}
          icon={<KeyRound className="size-4" aria-hidden="true" />}
        >
          Guardar contraseña
        </Button>
      </form>
    </AuthLayout>
  );
}

function InvalidLink() {
  return (
    <div className="space-y-4">
      <p className="text-muted">
        Este enlace no es válido, expiró o ya fue usado. Los enlaces de invitación y de restablecimiento sirven una sola
        vez y por tiempo limitado. Pide uno nuevo a un administrador de SENTINEL.
      </p>
      <ButtonLink to="/login" variant="primary" className="w-full">
        Ir al ingreso
      </ButtonLink>
    </div>
  );
}
